/**
 * Pinning the tag-rule scope, for places that ALREADY know whose rules these are.
 * For Media Devoted | Created By Ciro Autiero
 *
 * The admin page lets you pick a country → campaign and keeps it in the URL.
 * Inside the Flow Optimizer drawer there is nothing to pick: you opened one
 * campaign, and its rules are the subject. Pinning lets the very same rules
 * list render there, scoped correctly, with no picker and no URL writes —
 * rather than a second, lesser copy of the same screen.
 */

import { createContext, useContext, useMemo, type FC, type ReactNode } from 'react';

import type { TagRuleScope } from './model';

const PinnedScopeContext = createContext<TagRuleScope | null>(null);

/** The pinned scope, or null when the caller is free to choose one. */
export function usePinnedTagRuleScope(): TagRuleScope | null {
  return useContext(PinnedScopeContext);
}

export const TagRuleScopePin: FC<{ scope: TagRuleScope; children: ReactNode }> = ({
  scope,
  children,
}) => {
  // Rebuilt from the PARTS, not passed through: the FO drawer makes a fresh
  // scope object on every preview refresh, and an unstable context value would
  // re-render the whole rules list underneath it on each one.
  const { countryKey, provider, campaignId } = scope;
  const value = useMemo<TagRuleScope>(
    () => ({ countryKey, provider, campaignId }),
    [countryKey, provider, campaignId],
  );
  return (
    <PinnedScopeContext.Provider value={value}>{children}</PinnedScopeContext.Provider>
  );
};
